// Media library client — /admin/api/media.
//
// HAND-WRITTEN rather than orval-generated, deliberately: `openapi.json` is a
// committed snapshot refreshed by `pnpm spec:pull` against a RUNNING backend,
// and these endpoints are not in it yet. Regenerating is also not free today —
// orval runs with `clean: true` and would delete the hand-extended finance
// model files that the snapshot no longer describes. Once the backend is
// deployed, `pnpm spec:pull && pnpm codegen` can replace this file wholesale;
// the hook names and query keys below already follow the generated convention
// (`[url, params?]`) so that swap stays mechanical.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/api/http';
import { unwrap, type SuccessResponseDTO } from '@/api/envelope';

const PACKAGES_URL = '/admin/api/media/packages';
const STICKERS_URL = '/admin/api/media/stickers';

/** Bare-URL keys, prefix-matching every variant — same shape orval emits. */
export const MEDIA_PACKAGES_QUERY_KEY = [PACKAGES_URL] as const;
export const MEDIA_STICKERS_QUERY_KEY = [STICKERS_URL] as const;

// ------------------------------------------------------------------- types

/** Effects and transitions are ONE catalog; `kind` is the caps-derived split. */
export type MediaPackageKind = 'EFFECT' | 'TRANSITION' | 'UNKNOWN';
export type MediaPackageSource = 'PUBLISHED' | 'ADOPTED' | 'FACTORY';
export type CurationStatus = 'ACTIVE' | 'DELISTED' | 'RETIRED';

export interface MediaPackage {
  packageId: string;
  version: string;
  path: string;
  sha256: string;
  bytes: number;
  kind: MediaPackageKind;
  /** The package's own `kind` field — informational, never the classifier. */
  rawKind?: string | null;
  caps?: string[] | null;
  name?: string | null;
  category?: string | null;
  licenseSpdx?: string | null;
  licenseProvenance?: string | null;
  assetCount: number;
  goldenFrames: number;
  source: MediaPackageSource;
  status: CurationStatus;
  /** True while the offline factory could restore this package on its next run. */
  factoryManaged: boolean;
  modifiedAt?: string | null;
}

export interface MediaPackagePreview {
  packageId: string;
  version: string;
  frameUrls: string[];
}

export type StickerSource = 'MANAGED' | 'CATALOG';

export interface MediaSticker {
  stickerId: string;
  packId: string;
  name: string;
  /** Original artwork; fetched only for an explicit download or placement. */
  downloadUrl: string;
  thumbnailUrl?: string | null;
  keywords?: string[] | null;
  tags?: string[] | null;
  style?: string | null;
  styleName?: string | null;
  relatedCategoryIds?: string[] | null;
  sortOrder?: number | null;
  sourceId?: string | null;
  originalWidth: number;
  originalHeight: number;
  bytes?: number | null;
  source: StickerSource;
  status: CurationStatus;
  modifiedAt?: string | null;
}

export interface MediaStickerPack {
  packId: string;
  displayName: string;
  sortOrder: number;
  source: StickerSource;
  status: CurationStatus;
  activeStickerCount: number;
  delistedStickerCount: number;
  /** The mobile picker uses this exact image as the pack's tab icon. */
  tabIconUrl?: string | null;
  modifiedAt?: string | null;
}

export interface MediaStickerLibrary {
  packs: MediaStickerPack[];
  stickers: MediaSticker[];
}

// ---------------------------------------------------------------- fetchers

async function get<T>(url: string): Promise<T> {
  const res = await http.get<SuccessResponseDTO<T>>(url);
  return unwrap<T>(res.data);
}

async function patch(url: string): Promise<void> {
  await http.patch(url);
}

// ------------------------------------------------------------------- hooks

/** Every .tfx package the CDN serves, merged with its curation state. */
export function useMediaPackages() {
  return useQuery({
    queryKey: MEDIA_PACKAGES_QUERY_KEY,
    queryFn: () => get<MediaPackage[]>(PACKAGES_URL),
  });
}

/**
 * Golden frames for one package, fetched only when a preview is open — the
 * backend lists S3 and presigns per frame, so this is not free.
 */
export function useMediaPackagePreview(packageId: string | null) {
  return useQuery({
    queryKey: [`${PACKAGES_URL}/preview`, packageId],
    queryFn: () =>
      get<MediaPackagePreview>(`${PACKAGES_URL}/${encodeURIComponent(packageId ?? '')}/preview`),
    enabled: !!packageId,
  });
}

/** Delist/relist a package. Both rebuild the mobile catalog backend-side. */
export function useSetPackageListed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, listed }: { packageId: string; listed: boolean }) =>
      patch(`${PACKAGES_URL}/${encodeURIComponent(packageId)}/${listed ? 'relist' : 'delist'}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_PACKAGES_QUERY_KEY }),
  });
}

/** Packs and stickers together — see MediaStickerLibraryDTO for why. */
export function useStickerLibrary() {
  return useQuery({
    queryKey: MEDIA_STICKERS_QUERY_KEY,
    queryFn: () => get<MediaStickerLibrary>(STICKERS_URL),
  });
}

export function useStickerArchive(enabled: boolean) {
  return useQuery({
    queryKey: [...MEDIA_STICKERS_QUERY_KEY, 'archive'],
    queryFn: () => get<MediaStickerLibrary>(`${STICKERS_URL}/archive`),
    enabled,
  });
}

export interface StickerMetadata {
  name: string;
  keywords: string[];
  tags: string[];
  style: string | null;
  styleName: string | null;
  relatedCategoryIds: string[];
  sortOrder: number;
}

export function useUpdateStickerMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stickerId, metadata }: { stickerId: string; metadata: StickerMetadata }) =>
      http.patch(`${STICKERS_URL}/${encodeURIComponent(stickerId)}/metadata`, metadata),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY }),
  });
}

export function useUpsertStickerPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { packId: string; displayName: string; sortOrder?: number }) =>
      http.post(`${STICKERS_URL}/packs`, body).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY }),
  });
}

export function useSetStickerPackListed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packId, listed }: { packId: string; listed: boolean }) =>
      patch(`${STICKERS_URL}/packs/${encodeURIComponent(packId)}/${listed ? 'relist' : 'delist'}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY }),
  });
}

export function useSetStickerListed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stickerId, listed }: { stickerId: string; listed: boolean }) =>
      patch(`${STICKERS_URL}/${encodeURIComponent(stickerId)}/${listed ? 'relist' : 'delist'}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY }),
  });
}

// ------------------------------------------------------------------ upload

/** New uploads use PNG so the server can verify alpha and create a thumbnail. */
export const STICKER_ACCEPT = 'image/png,.png';
export const STICKER_MAX_BYTES = 2 * 1024 * 1024;

export interface PreparedSticker {
  file: File;
  /** Slug derived from the filename — becomes part of a public URL. */
  name: string;
  contentType: string;
  width: number;
  height: number;
}

/**
 * The backend's ASSET_NAME charset is strict (the name is interpolated into an
 * S3 key AND into the downloadUrl the mobile client fetches verbatim), so
 * normalise whatever the browser hands us before anything is uploaded.
 */
export function sanitizeStickerName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const slug = stem
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .replace(/[.\-_]+$/, '')
    .slice(0, 64);
  return slug || 'sticker';
}

/**
 * Read intrinsic size for upload feedback. The server independently verifies
 * the PNG dimensions and transparency against these declared values.
 */
export function measureImage(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error(`Could not read the dimensions of ${file.name}`));
        return;
      }
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} is not a readable image`));
    };
    img.src = url;
  });
}

/** Validate + measure a browser File, or throw with an actionable reason. */
export async function prepareSticker(file: File): Promise<PreparedSticker> {
  const contentType = file.type;
  if (contentType !== 'image/png') {
    throw new Error(`${file.name}: new stickers must be transparent PNGs`);
  }
  if (file.size > STICKER_MAX_BYTES) {
    throw new Error(`${file.name}: exceeds the ${STICKER_MAX_BYTES / 1024} KB limit`);
  }
  const { width, height } = await measureImage(file);
  return { file, name: sanitizeStickerName(file.name), contentType, width, height };
}

/**
 * Upload one prepared sticker. The raw image is the request body (the backend
 * consumes image/png directly), with metadata as query params.
 */
export function useUploadSticker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packId, sticker }: { packId: string; sticker: PreparedSticker }) =>
      http
        .post<SuccessResponseDTO<MediaSticker>>(
          `${STICKERS_URL}/packs/${encodeURIComponent(packId)}/assets`,
          sticker.file,
          {
            headers: { 'Content-Type': sticker.contentType },
            params: {
              name: sticker.name,
              originalWidth: sticker.width,
              originalHeight: sticker.height,
            },
          },
        )
        .then((res) => unwrap<MediaSticker>(res.data)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY }),
  });
}
